import React from "react";
import type { Fleet } from "../../types/auth";
import { Edit2, ChevronUp, ChevronDown } from "lucide-react";

interface FleetTableProps {
  fleets: Fleet[];
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (column: string) => void;
  onEdit: (fleet: Fleet) => void;
}

export const FleetTable: React.FC<FleetTableProps> = ({
  fleets,
  sortBy,
  sortOrder,
  onSort,
  onEdit,
}) => {
  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const ariaSortFor = (column: string): "none" | "ascending" | "descending" => {
    if (sortBy !== column) return "none";
    return sortOrder === "asc" ? "ascending" : "descending";
  };

  const headerClass =
    "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100";

  const sortButtonClass =
    "inline-flex items-center w-full bg-transparent border-0 p-0 m-0 font-inherit text-inherit uppercase tracking-wider cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset rounded";

  if (fleets.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
        No fleets found
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className={headerClass} aria-sort={ariaSortFor("name")}>
              <button
                type="button"
                className={sortButtonClass}
                onClick={() => onSort("name")}
              >
                Name
                <SortIcon column="name" />
              </button>
            </th>
            <th
              className={headerClass}
              aria-sort={ariaSortFor("vehicle_count")}
            >
              <button
                type="button"
                className={sortButtonClass}
                onClick={() => onSort("vehicle_count")}
              >
                Vehicles
                <SortIcon column="vehicle_count" />
              </button>
            </th>
            <th className={headerClass} aria-sort={ariaSortFor("created_at")}>
              <button
                type="button"
                className={sortButtonClass}
                onClick={() => onSort("created_at")}
              >
                Created
                <SortIcon column="created_at" />
              </button>
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {fleets.map((fleet) => (
            <tr key={fleet.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                {fleet.name}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {fleet.vehicle_count ?? 0}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {new Date(fleet.created_at).toLocaleString()}
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  type="button"
                  onClick={() => onEdit(fleet)}
                  className="inline-flex items-center px-2 py-1 text-sm text-blue-600 hover:text-blue-800"
                  title="Edit fleet"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
